<script>
  import { onMount } from "svelte";
  import { apiRequest } from "../utils/api";

  export let isAdmin = false;
  // URL-owned form state (owned by AppShell; a form open is a history entry so the
  // browser Back button closes it, and each form deep-links).
  export let projectMode = null;    // null | "new" | "edit"
  export let projectEditId = null;  // number | null
  export let templateMode = null;   // null | "new" | "edit"
  export let templateEditId = null; // number | null
  export let onProjectNew = () => {};
  export let onProjectEdit = () => {};
  export let onProjectsClose = () => {};
  export let onTemplateNew = () => {};
  export let onTemplateClose = () => {};
  export let onTemplateEdit = () => {};

  let projects = [];
  let templates = [];
  let loading = false;
  let listError = null;

  let projectForm = { name: "", project_category: "", currency_name: "BOWS", currency_rate: 10, target_balance: 0, system_prompt: "" };
  let creatingProject = false;
  let projectError = null;
  let projectMessage = null;
  let projectEditForm = null;
  let editFormForId = null;   // the project id projectEditForm was populated from
  let updatingProject = false;

  let templateForm = { name: "", project_id: "", target_url: "", email_target_url: "", bundle: null, email_bundle: null };
  let templateFileInput = null;
  let templateEmailFileInput = null;
  let uploadingTemplate = false;
  let templateError = null;
  let templateMessage = null;
  let templateEditForm = null;
  let templateEditFormForId = null; // the template id templateEditForm was populated from
  let updatingTemplate = false;

  // Form visibility derives from the URL-owned mode props.
  $: showProjectForm = projectMode === "new";
  $: showTemplateForm = templateMode === "new";
  $: editingProjectId = projectMode === "edit" ? projectEditId : null;
  $: editingTemplateId = templateMode === "edit" ? templateEditId : null;

  // Populate the edit form from the loaded project on entering edit mode (or once
  // the list arrives after a deep-link). Guarded by editFormForId so a background
  // list refresh doesn't clobber an in-progress edit.
  $: syncEditForm(editingProjectId, projects);
  function syncEditForm(id, list) {
    if (!id) {
      projectEditForm = null;
      editFormForId = null;
      return;
    }
    if (editFormForId === id) {
      return;
    }
    const project = list.find((entry) => entry.id === id);
    if (!project) {
      return; // list not loaded yet (deep-link) — repopulates once it arrives
    }
    projectEditForm = {
      name: project.name,
      project_category: project.project_category || "",
      currency_name: project.currency_name || "BOWS",
      currency_rate: project.currency_rate ?? 1,
      target_balance: project.target_balance ?? 0,
      system_prompt: project.system_prompt || ""
    };
    editFormForId = id;
  }

  // Same guard pattern for the template edit sub-mode (name + target_url only).
  $: syncTemplateEditForm(editingTemplateId, templates);
  function syncTemplateEditForm(id, list) {
    if (!id) {
      templateEditForm = null;
      templateEditFormForId = null;
      return;
    }
    if (templateEditFormForId === id) {
      return;
    }
    const template = list.find((entry) => entry.id === id);
    if (!template) {
      return; // list not loaded yet (deep-link) — repopulates once it arrives
    }
    templateEditForm = {
      name: template.name,
      target_url: template.target_url || "",
      email_target_url: template.email_target_url || ""
    };
    templateEditFormForId = id;
  }

  $: categoryNames = [ ...new Set(projects.map((project) => project.project_category).filter(Boolean)) ];

  // Live exchange-rate preview for the create form. currency_rate is units-per-1-EUR,
  // so both directions are derivable: 1 EUR = rate units, and 1 unit = 1/rate EUR.
  $: rateNum = Number(projectForm.currency_rate);
  $: currencyLabel = projectForm.currency_name?.trim() || "BOWS";
  $: currencyUnit = currencyLabel.replace(/s$/i, ""); // BOWS → BOW for the singular side
  $: eurPerUnit = rateNum > 0 ? 1 / rateNum : null;

  function trimNum(value) {
    // Show up to 4 dp but drop trailing zeros so 0.10 → "0.1", 10 → "10".
    return Number(value.toFixed(4)).toString();
  }

  onMount(() => {
    if (isAdmin) {
      loadAll();
    }
  });

  async function loadAll() {
    loading = true;
    listError = null;
    try {
      [ projects, templates ] = await Promise.all([
        apiRequest("/api/projects"),
        apiRequest("/api/templates")
      ]);
    } catch (error) {
      listError = error?.message || "Could not load projects.";
    } finally {
      loading = false;
    }
  }

  async function createProject(event) {
    event.preventDefault();
    if (creatingProject) {
      return;
    }
    creatingProject = true;
    projectError = null;
    projectMessage = null;
    try {
      const created = await apiRequest("/api/projects", { method: "POST", body: { project: projectForm } });
      projectMessage = `Created ${created.name}.`;
      projectForm = { name: "", project_category: "", currency_name: "BOWS", currency_rate: 1, target_balance: 0, system_prompt: "" };
      onProjectsClose();
      await loadAll();
    } catch (error) {
      projectError = error?.message || "Could not create project.";
    } finally {
      creatingProject = false;
    }
  }

  function editProject(project) {
    projectError = null;
    projectMessage = null;
    onProjectEdit(project.id); // URL-drives the edit form open (Back closes it)
  }

  function cancelProjectEdit() {
    onProjectsClose();
  }

  async function updateProject(event) {
    event.preventDefault();
    if (updatingProject || !editingProjectId) {
      return;
    }

    updatingProject = true;
    projectError = null;
    projectMessage = null;
    try {
      const updated = await apiRequest(`/api/projects/${editingProjectId}`, {
        method: "PATCH",
        body: { project: projectEditForm }
      });
      projectMessage = `Updated ${updated.name}.`;
      cancelProjectEdit();
      await loadAll();
    } catch (error) {
      projectError = error?.message || "Could not update project.";
    } finally {
      updatingProject = false;
    }
  }

  async function uploadTemplate(event) {
    event.preventDefault();
    if (uploadingTemplate) {
      return;
    }
    if (!templateForm.project_id) {
      templateError = "Pick a project first.";
      return;
    }
    if (!templateForm.bundle) {
      templateError = "Choose a bundle .zip.";
      return;
    }
    uploadingTemplate = true;
    templateError = null;
    templateMessage = null;

    const data = new FormData();
    data.append("template[name]", templateForm.name);
    data.append("template[project_id]", templateForm.project_id);
    data.append("template[bundle]", templateForm.bundle);
    // The email bundle is optional — only send it when the admin picked a file.
    if (templateForm.email_bundle) {
      data.append("template[email_bundle]", templateForm.email_bundle);
    }
    // The site URL is optional — becomes the generation's render origin (editable
    // later per generation). Only send it when the admin filled it in.
    if (templateForm.target_url) {
      data.append("template[target_url]", templateForm.target_url);
    }
    // A separate render origin for the confirmation emails (falls back to
    // target_url when blank). Optional, same as target_url above.
    if (templateForm.email_target_url) {
      data.append("template[email_target_url]", templateForm.email_target_url);
    }

    try {
      const created = await apiRequest("/api/templates", { method: "POST", body: data });
      templateMessage = `Uploaded ${created.name}.`;
      templateForm = { name: "", project_id: "", target_url: "", email_target_url: "", bundle: null, email_bundle: null };
      if (templateFileInput) {
        templateFileInput.value = "";
      }
      if (templateEmailFileInput) {
        templateEmailFileInput.value = "";
      }
      onTemplateClose();
      await loadAll();
    } catch (error) {
      templateError = error?.message || "Could not upload template.";
    } finally {
      uploadingTemplate = false;
    }
  }

  function projectName(id) {
    return projects.find((project) => project.id === id)?.name || "—";
  }

  function editTemplate(template) {
    templateError = null;
    templateMessage = null;
    onTemplateEdit(template.id); // URL-drives the edit form open (Back closes it)
  }

  function cancelTemplateEdit() {
    onTemplateClose();
  }

  async function updateTemplate(event) {
    event.preventDefault();
    if (updatingTemplate || !editingTemplateId) {
      return;
    }

    updatingTemplate = true;
    templateError = null;
    templateMessage = null;
    try {
      const updated = await apiRequest(`/api/templates/${editingTemplateId}`, {
        method: "PATCH",
        body: { template: templateEditForm }
      });
      templateMessage = `Updated ${updated.name}.`;
      cancelTemplateEdit();
      await loadAll();
    } catch (error) {
      templateError = error?.message || "Could not update template.";
    } finally {
      updatingTemplate = false;
    }
  }
</script>

{#if !isAdmin}
  <p class="text-neutral-500 text-xs">Admins only.</p>
{:else}
  <div class="space-y-6">
    <div class="flex justify-end gap-2">
      <button type="button" class="btn-secondary py-1 text-xs" on:click={() => (showProjectForm ? onProjectsClose() : onProjectNew())} data-testid="new-project-toggle">
        {showProjectForm ? "Cancel" : "+ New project"}
      </button>
      <button type="button" class="btn-secondary py-1 text-xs" on:click={() => (showTemplateForm ? onTemplateClose() : onTemplateNew())} data-testid="new-template-toggle">
        {showTemplateForm ? "Cancel" : "+ Upload template"}
      </button>
    </div>

    <!-- Banners live outside the collapsible forms so a create/upload result stays
         visible after its form auto-collapses on success. -->
    {#if projectError}
      <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-3 py-1.5 text-xs" role="alert" data-testid="project-error">{projectError}</p>
    {/if}
    {#if projectMessage}
      <p class="bg-success-bg text-success-text border border-success-border/20 rounded px-3 py-1.5 text-xs" role="status">{projectMessage}</p>
    {/if}
    {#if templateError}
      <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-3 py-1.5 text-xs" role="alert" data-testid="template-error">{templateError}</p>
    {/if}
    {#if templateMessage}
      <p class="bg-success-bg text-success-text border border-success-border/20 rounded px-3 py-1.5 text-xs" role="status">{templateMessage}</p>
    {/if}

    {#if showProjectForm}
    <form class="bg-neutral-50 border border-neutral-200 rounded-lg p-4" on:submit={createProject}>
      <h3 class="text-xs font-bold text-neutral-800 uppercase tracking-wider mb-3">Create Project</h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div class="form-group">
          <label class="form-label" for="project-name-input">Project Name</label>
          <input id="project-name-input" class="form-input" type="text" placeholder="e.g. Acme Corp" bind:value={projectForm.name} required data-testid="project-name" />
        </div>
        <div class="form-group">
          <label class="form-label" for="project-category-input">Category <span class="text-neutral-400 font-normal">· optional</span></label>
          <input
            id="project-category-input"
            class="form-input"
            type="text"
            list="category-options"
            placeholder="e.g. Invoices"
            bind:value={projectForm.project_category}
            data-testid="project-category"
          />
          <datalist id="category-options">
            {#each categoryNames as name}<option value={name}></option>{/each}
          </datalist>
          <p class="text-[0.68rem] text-neutral-500 mt-1">A grouping label shown only in the projects list and the generation header — it affects nothing else.</p>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        <div class="form-group">
          <label class="form-label" for="project-currency">Currency</label>
          <input id="project-currency" class="form-input" type="text" bind:value={projectForm.currency_name} />
          <p class="text-[0.68rem] text-neutral-500 mt-1">The token label printed on the statement.</p>
        </div>
        <div class="form-group">
          <label class="form-label" for="project-rate">Exchange rate</label>
          <input id="project-rate" class="form-input" type="number" step="any" min="0" bind:value={projectForm.currency_rate} />
        </div>
        <button type="submit" class="btn-primary w-full" disabled={creatingProject}>
          {creatingProject ? "Creating…" : "Create project"}
        </button>
      </div>
      <p class="text-[0.68rem] text-neutral-500 mt-2" data-testid="rate-hint">
        <strong>Exchange rate</strong> — units of <strong>{currencyLabel}</strong> per 1&nbsp;EUR.
        {#if eurPerUnit !== null}
          <span class="text-neutral-700 font-medium">1&nbsp;EUR = {trimNum(rateNum)}&nbsp;{currencyLabel}</span>
          &nbsp;·&nbsp;
          <span class="text-neutral-700 font-medium">1&nbsp;{currencyUnit} = €{trimNum(eurPerUnit)}</span>
        {:else}
          Enter a rate above 0 to preview the conversion.
        {/if}
      </p>
      <p class="text-[0.68rem] text-neutral-500 mt-1">Converts imported EUR top-ups into whole tokens, derives the EUR figure for token-only rows, and feeds the battle-consistency guardrail.</p>
    </form>
    {/if}

    {#if showTemplateForm}
    <form class="bg-neutral-50 border border-neutral-200 rounded-lg p-4" on:submit={uploadTemplate}>
      <h3 class="text-xs font-bold text-neutral-800 uppercase tracking-wider mb-1">Upload Template</h3>
      <p class="text-[0.72rem] text-neutral-500 mb-3">A template is one or two vendor zip bundles tied to a project: a <strong>statement bundle</strong> (required) renders the report pages, and an optional <strong>email bundle</strong> renders the per-row confirmation emails.</p>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
        <div class="form-group">
          <label class="form-label" for="tmpl-name">Template Name</label>
          <input id="tmpl-name" class="form-input" type="text" placeholder="e.g. Invoice v1" bind:value={templateForm.name} required data-testid="template-name" />
        </div>
        <div class="form-group">
          <label class="form-label" for="tmpl-project">Project</label>
          <select id="tmpl-project" class="form-input" bind:value={templateForm.project_id} required data-testid="template-project">
            <option value="" disabled>Pick a project…</option>
            {#each projects as project}<option value={project.id}>{project.name}</option>{/each}
          </select>
        </div>
        <div class="form-group">
          <label class="form-label" for="tmpl-file">Statement bundle (.zip)</label>
          <input
            id="tmpl-file"
            class="form-input file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-[0.72rem] file:font-semibold file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100"
            type="file"
            accept=".zip,application/zip"
            bind:this={templateFileInput}
            on:change={(event) => (templateForm.bundle = event.target.files?.[0] || null)}
            data-testid="template-bundle"
          />
          <p class="text-[0.68rem] text-neutral-500 mt-1">Renders the statement / report pages (required).</p>
        </div>
        <div class="form-group">
          <label class="form-label" for="tmpl-email-file">Email bundle (.zip) <span class="text-neutral-400 font-normal">· optional</span></label>
          <input
            id="tmpl-email-file"
            class="form-input file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-[0.72rem] file:font-semibold file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100"
            type="file"
            accept=".zip,application/zip"
            bind:this={templateEmailFileInput}
            on:change={(event) => (templateForm.email_bundle = event.target.files?.[0] || null)}
            data-testid="template-email-bundle"
          />
          <p class="text-[0.68rem] text-neutral-500 mt-1">Renders the per-row confirmation emails. No email bundle ⇒ no emails.</p>
        </div>
        <div class="form-group">
          <label class="form-label" for="tmpl-target-url">Target URL <span class="text-neutral-400 font-normal">· optional</span></label>
          <input
            id="tmpl-target-url"
            class="form-input"
            type="text"
            placeholder="e.g. https://mybank.example"
            bind:value={templateForm.target_url}
            data-testid="template-target-url"
          />
          <p class="text-[0.68rem] text-neutral-500 mt-1">Full site URL incl. <code class="text-neutral-600">https://</code> (goproxy serves the report from its domain). Becomes the render origin for generations made from this template — editable later per generation.</p>
        </div>
        <div class="form-group">
          <label class="form-label" for="tmpl-email-target-url">Email render URL <span class="text-neutral-400 font-normal">· optional</span></label>
          <input
            id="tmpl-email-target-url"
            class="form-input"
            type="text"
            placeholder="e.g. https://mail.mybank.example"
            bind:value={templateForm.email_target_url}
            data-testid="template-email-target-url"
          />
          <p class="text-[0.68rem] text-neutral-500 mt-1">A separate render origin for the confirmation-email artifacts (the admin log pages use Target URL). Inherited by generations; falls back to Target URL when blank.</p>
        </div>
      </div>
      <div class="flex justify-end mt-3">
        <button type="submit" class="btn-primary" disabled={uploadingTemplate}>
          {uploadingTemplate ? "Uploading…" : "Upload template"}
        </button>
      </div>
    </form>
    {/if}

    {#if listError}
      <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-3 py-1.5 text-xs" role="alert">{listError}</p>
    {/if}
    
    {#if loading}
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-pulse">
        <div class="space-y-3">
          <div class="h-4 bg-neutral-200 rounded w-16 mb-2"></div>
          <div class="border border-neutral-200 rounded-lg overflow-hidden">
            <div class="bg-neutral-50 h-8 border-b border-neutral-200"></div>
            <div class="divide-y divide-neutral-100">
              {#each Array(2) as _}
                <div class="flex p-3 space-x-4">
                  <div class="h-3.5 bg-neutral-200 rounded w-1/3"></div>
                  <div class="h-3.5 bg-neutral-200 rounded w-1/4"></div>
                  <div class="h-3.5 bg-neutral-200 rounded w-1/6"></div>
                </div>
              {/each}
            </div>
          </div>
        </div>
        <div class="space-y-3">
          <div class="h-4 bg-neutral-200 rounded w-20 mb-2"></div>
          <div class="border border-neutral-200 rounded-lg overflow-hidden">
            <div class="bg-neutral-50 h-8 border-b border-neutral-200"></div>
            <div class="divide-y divide-neutral-100">
              {#each Array(2) as _}
                <div class="flex p-3 space-x-4">
                  <div class="h-3.5 bg-neutral-200 rounded w-1/3"></div>
                  <div class="h-3.5 bg-neutral-200 rounded w-1/4"></div>
                  <div class="h-3.5 bg-neutral-200 rounded w-1/6"></div>
                </div>
              {/each}
            </div>
          </div>
        </div>
      </div>
    {:else}
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Projects List -->
        <div class="space-y-2">
          <h4 class="text-xs font-bold text-neutral-800 tracking-wider">Projects</h4>
          {#if projects.length === 0}
            <div class="bg-white border border-neutral-200 rounded-lg p-6 text-center space-y-2 shadow-2xs">
              <p class="text-neutral-500 text-xs font-semibold">No projects yet.</p>
              <p class="text-[0.7rem] text-neutral-400">Use the "Create Project" form above to initialize one.</p>
            </div>
          {:else}
            <div class="overflow-x-auto border border-neutral-200 rounded-lg">
              <table class="w-full text-left border-collapse text-xs" data-testid="project-table">
                <thead>
                  <tr class="bg-neutral-50 border-b border-neutral-200">
                    <th class="p-2.5 font-bold text-neutral-700">Name</th>
                    <th class="p-2.5 font-bold text-neutral-700">Category</th>
                    <th class="p-2.5 font-bold text-neutral-700">Currency</th>
                    <th class="p-2.5 font-bold text-neutral-700">Exchange rate</th>
                    <th class="p-2.5 font-bold text-neutral-700 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-neutral-100">
                  {#each projects as project (project.id)}
                    <tr class="hover:bg-neutral-50/50 transition-colors">
                      <td class="p-2.5 font-medium text-neutral-900">{project.name}</td>
                      <td class="p-2.5 text-neutral-600">{project.project_category || "—"}</td>
                      <td class="p-2.5 text-neutral-600">{project.currency_name || "—"}</td>
                      <td class="p-2.5 text-neutral-600">{project.currency_rate ?? "—"}<span class="text-neutral-400"> / EUR</span></td>
                      <td class="p-2.5 text-right">
                        <button
                          type="button"
                          class="btn-secondary py-1 text-xs"
                          on:click={() => editProject(project)}
                          data-testid={`edit-project-${project.id}`}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          {/if}
        </div>

        <!-- Templates List -->
        <div class="space-y-2">
          <h4 class="text-xs font-bold text-neutral-800 tracking-wider">Templates</h4>
          {#if templates.length === 0}
            <div class="bg-white border border-neutral-200 rounded-lg p-6 text-center space-y-2 shadow-2xs">
              <p class="text-neutral-500 text-xs font-semibold">No templates yet.</p>
              <p class="text-[0.7rem] text-neutral-400">Upload a vendor bundle zip linked to a project above.</p>
            </div>
          {:else}
            <div class="overflow-x-auto border border-neutral-200 rounded-lg">
              <table class="w-full text-left border-collapse text-xs" data-testid="template-table">
                <thead>
                  <tr class="bg-neutral-50 border-b border-neutral-200">
                    <th class="p-2.5 font-bold text-neutral-700">Name</th>
                    <th class="p-2.5 font-bold text-neutral-700">Project</th>
                    <th class="p-2.5 font-bold text-neutral-700">Target URL</th>
                    <th class="p-2.5 font-bold text-neutral-700">Statement bundle</th>
                    <th class="p-2.5 font-bold text-neutral-700">Email bundle</th>
                    <th class="p-2.5 font-bold text-neutral-700 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-neutral-100">
                  {#each templates as template (template.id)}
                    <tr class="hover:bg-neutral-50/50 transition-colors">
                      <td class="p-2.5 font-medium text-neutral-900">{template.name}</td>
                      <td class="p-2.5 text-neutral-600">{projectName(template.project_id)}</td>
                      <td class="p-2.5 text-neutral-600" data-testid="template-target-url-cell">
                        {template.target_url || "—"}
                      </td>
                      <td class="p-2.5">
                        {#if template.bundle_download_path}
                          <a
                            href={template.bundle_download_path}
                            class="inline-flex items-center gap-1 text-brand-600 underline hover:text-brand-700"
                            data-testid="template-bundle-download"
                          >
                            ⬇ zip
                          </a>
                        {:else}
                          <span class="text-neutral-400">—</span>
                        {/if}
                      </td>
                      <td class="p-2.5">
                        {#if template.email_bundle_download_path}
                          <a
                            href={template.email_bundle_download_path}
                            class="inline-flex items-center gap-1 text-brand-600 underline hover:text-brand-700"
                            data-testid="template-email-bundle-download"
                          >
                            ⬇ zip
                          </a>
                        {:else}
                          <span class="text-neutral-400">no email</span>
                        {/if}
                      </td>
                      <td class="p-2.5 text-right">
                        <button
                          type="button"
                          class="btn-secondary py-1 text-xs"
                          on:click={() => editTemplate(template)}
                          data-testid={`template-edit-${template.id}`}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          {/if}
        </div>
      </div>

      {#if editingProjectId && projectEditForm}
        <form class="bg-neutral-50 border border-neutral-200 rounded-lg p-4 mt-6" on:submit={updateProject} data-testid="project-edit-form">
          <h3 class="text-xs font-bold text-neutral-800 uppercase tracking-wider mb-3">Edit Project</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div class="form-group">
              <label class="form-label" for="edit-project-name">Project Name</label>
              <input id="edit-project-name" class="form-input" type="text" bind:value={projectEditForm.name} required />
            </div>
            <div class="form-group">
              <label class="form-label" for="edit-project-category">Category <span class="text-neutral-400 font-normal">· optional</span></label>
              <input id="edit-project-category" class="form-input" type="text" bind:value={projectEditForm.project_category} data-testid="edit-project-category" />
              <p class="text-[0.68rem] text-neutral-500 mt-1">Grouping label only (projects list + generation header) — affects nothing else.</p>
            </div>
            <div class="form-group">
              <label class="form-label" for="edit-project-currency">Currency</label>
              <input id="edit-project-currency" class="form-input" type="text" bind:value={projectEditForm.currency_name} required />
            </div>
            <div class="form-group">
              <label class="form-label" for="edit-project-rate">Exchange rate</label>
              <input id="edit-project-rate" class="form-input" type="number" step="any" min="0" bind:value={projectEditForm.currency_rate} required />
            </div>
          </div>
          <div class="form-group">
            <label class="form-label" for="project-system-prompt">System prompt</label>
            <textarea
              id="project-system-prompt"
              class="form-input min-h-40 font-mono text-xs"
              rows="8"
              bind:value={projectEditForm.system_prompt}
              placeholder="Leave blank to use the built-in generation workflow prompt."
              data-testid="project-system-prompt"
            ></textarea>
            <p class="text-[0.68rem] text-neutral-500 mt-1">Applied to new agent runs for this project. Leave blank to use the built-in default.</p>
          </div>
          <div class="flex justify-end gap-2 mt-3">
            <button type="button" class="btn-secondary" on:click={cancelProjectEdit}>Cancel</button>
            <button type="submit" class="btn-primary" disabled={updatingProject} data-testid="save-project">
              {updatingProject ? "Saving…" : "Save project"}
            </button>
          </div>
        </form>
      {/if}

      {#if editingTemplateId && templateEditForm}
        <form class="bg-neutral-50 border border-neutral-200 rounded-lg p-4 mt-6" on:submit={updateTemplate} data-testid="template-edit-form">
          <h3 class="text-xs font-bold text-neutral-800 uppercase tracking-wider mb-1">Edit Template</h3>
          <p class="text-[0.72rem] text-neutral-500 mb-3">Templates are otherwise frozen after upload — only the name and target URL can be changed here.</p>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div class="form-group">
              <label class="form-label" for="edit-template-name">Template Name</label>
              <input id="edit-template-name" class="form-input" type="text" bind:value={templateEditForm.name} required data-testid="edit-template-name" />
            </div>
            <div class="form-group">
              <label class="form-label" for="edit-template-target-url">Target URL <span class="text-neutral-400 font-normal">· optional</span></label>
              <input id="edit-template-target-url" class="form-input" type="text" placeholder="e.g. https://mybank.example" bind:value={templateEditForm.target_url} data-testid="edit-template-target-url" />
              <p class="text-[0.68rem] text-neutral-500 mt-1">Becomes the render origin for NEW generations made from this template. Existing generations keep their own value (editable per generation).</p>
            </div>
            <div class="form-group">
              <label class="form-label" for="edit-template-email-target-url">Email render URL <span class="text-neutral-400 font-normal">· optional</span></label>
              <input id="edit-template-email-target-url" class="form-input" type="text" placeholder="e.g. https://mail.mybank.example" bind:value={templateEditForm.email_target_url} data-testid="edit-template-email-target-url" />
              <p class="text-[0.68rem] text-neutral-500 mt-1">A separate render origin for the confirmation emails on NEW generations (falls back to Target URL when blank).</p>
            </div>
          </div>
          <div class="flex justify-end gap-2 mt-3">
            <button type="button" class="btn-secondary" on:click={cancelTemplateEdit}>Cancel</button>
            <button type="submit" class="btn-primary" disabled={updatingTemplate} data-testid="save-template">
              {updatingTemplate ? "Saving…" : "Save template"}
            </button>
          </div>
        </form>
      {/if}

      <!-- Full-width section help -->
      <section class="border-t border-neutral-200 pt-4 mt-6 space-y-3">
        <h4 class="text-xs font-bold text-neutral-800 tracking-wider">About this section</h4>
        <p class="text-[0.72rem] text-neutral-500 leading-relaxed">
          A <strong>project</strong> is a self-contained account: its own currency, exchange rate, product catalog and rules. A <strong>template</strong> is a vendor zip bundle (<code class="font-mono">template.liquid</code> + assets) tied to a project that renders a generation into the final artifact. You create the project and upload its template here; the catalog and per-generation settings live under Catalog and Generations.
        </p>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3">
          <div class="space-y-1">
            <p class="text-[0.7rem] font-bold text-neutral-700">Exchange rate</p>
            <p class="text-[0.68rem] text-neutral-500 leading-relaxed">
              Units of the project currency per <strong>1&nbsp;EUR</strong> (rate <code class="font-mono">10</code> ⇒ 1&nbsp;EUR = 10&nbsp;BOWS, so 1&nbsp;BOW = €0.10). Used to convert imported top-ups (given in EUR) into whole BOWS, to derive the EUR figure for BOWS-only rows, and by the battle-consistency guardrail.
            </p>
          </div>
          <div class="space-y-1">
            <p class="text-[0.7rem] font-bold text-neutral-700">Why EUR and BOWS both travel</p>
            <p class="text-[0.68rem] text-neutral-500 leading-relaxed">
              BOWS are whole tokens, so <code class="font-mono">eur × rate</code> is rounded and lossy — €3.45 → 35&nbsp;BOWS → €3.50 back. Storing both keeps the exact authored figures on each side, lets fixed prices sit slightly off the nominal rate (promos), and lets the statement print both columns verbatim.
            </p>
          </div>
          <div class="space-y-1">
            <p class="text-[0.7rem] font-bold text-neutral-700">Target balance</p>
            <p class="text-[0.68rem] text-neutral-500 leading-relaxed">
              Set per <strong>generation</strong> (Generations → Edit settings), not per project — that's the guardrail enforced at completion, so different generations of one project can target different balances.
            </p>
          </div>
        </div>
      </section>
    {/if}
  </div>
{/if}
